import React, { useState } from 'react';
import ArtemisGeneticEvolution from './ArtemisGeneticEvolution';

const ArtemisSoftwareEvolution = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Software Evolution Card Component
  const SoftwareCard = ({ version, color, title, subtitle, timeline, icon, optimizations, examples }) => (
    <div style={{
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '16px',
      padding: '30px',
      border: `2px solid ${color}40`,
      position: 'relative',
      transition: 'all 0.3s ease',
      height: '100%'
    }}
    onMouseEnter={e => {
      e.currentTarget.style.transform = 'translateY(-5px)';
      e.currentTarget.style.boxShadow = `0 10px 30px ${color}30`;
    }}
    onMouseLeave={e => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}>
      <div style={{
        position: 'absolute',
        top: '-15px',
        left: '30px',
        background: '#0F2027',
        padding: '8px 20px',
        borderRadius: '20px',
        fontSize: '16px',
        fontWeight: '600',
        color: color,
        border: `2px solid ${color}`
      }}>
        {version}
      </div>
      
      <div style={{ marginTop: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
          <span style={{ fontSize: '40px' }}>{icon}</span>
          <div>
            <h3 style={{ margin: 0, fontSize: '24px', color: color }}>{title}</h3>
            <p style={{ margin: '5px 0 0 0', fontSize: '16px', opacity: 0.8 }}>{subtitle}</p>
          </div>
        </div>
        
        <div style={{
          background: 'rgba(0, 0, 0, 0.3)',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.7, marginBottom: '5px' }}>Timeline</div>
          <div style={{ fontSize: '18px', fontWeight: '600', color: color }}>{timeline}</div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ color: color, marginBottom: '15px' }}>🚀 Artemis Optimizes:</h4>
          <ul style={{ margin: 0, paddingLeft: '25px', lineHeight: '1.8' }}>
            {optimizations.map((opt, idx) => (
              <li key={idx} style={{ marginBottom: '8px' }}>{opt}</li>
            ))}
          </ul>
        </div>

        {examples && (
          <div style={{
            background: `${color}15`,
            padding: '15px',
            borderRadius: '8px',
            border: `1px solid ${color}30`
          }}>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px', color: color }}>
              Example Use Cases:
            </div>
            <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
              {examples.join(' • ')}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0F2027 0%, #203A43 50%, #2C5364 100%)',
      color: 'white',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      padding: '40px 20px',
      fontSize: '16px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <h1 style={{ 
          margin: '0 0 15px 0', 
          fontSize: '42px',
          fontWeight: '700',
          background: 'linear-gradient(135deg, #9B59B6, #8E44AD)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Evolution of Software with Artemis
        </h1>
        <p style={{ margin: '0 0 30px 0', fontSize: '20px', opacity: 0.8 }}>
          How Artemis adapts to optimize every software paradigm
        </p>
        
        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '20px',
          marginBottom: '40px',
          borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
          paddingBottom: '0'
        }}>
          <button
            onClick={() => setActiveTab('overview')}
            style={{
              background: 'none',
              border: 'none',
              color: activeTab === 'overview' ? '#9B59B6' : 'rgba(255, 255, 255, 0.6)',
              fontSize: '18px',
              fontWeight: '600',
              padding: '12px 24px',
              cursor: 'pointer',
              borderBottom: activeTab === 'overview' ? '3px solid #9B59B6' : '3px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.3s ease'
            }}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('software1')}
            style={{
              background: 'none',
              border: 'none',
              color: activeTab === 'software1' ? '#3498DB' : 'rgba(255, 255, 255, 0.6)',
              fontSize: '18px',
              fontWeight: '600',
              padding: '12px 24px',
              cursor: 'pointer',
              borderBottom: activeTab === 'software1' ? '3px solid #3498DB' : '3px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.3s ease'
            }}
          >
            Software 1.0
          </button>
          <button
            onClick={() => setActiveTab('software2')}
            style={{
              background: 'none',
              border: 'none',
              color: activeTab === 'software2' ? '#E74C3C' : 'rgba(255, 255, 255, 0.6)',
              fontSize: '18px',
              fontWeight: '600',
              padding: '12px 24px',
              cursor: 'pointer',
              borderBottom: activeTab === 'software2' ? '3px solid #E74C3C' : '3px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.3s ease'
            }}
          >
            Software 2.0
          </button>
          <button
            onClick={() => setActiveTab('software3')}
            style={{
              background: 'none',
              border: 'none',
              color: activeTab === 'software3' ? '#27AE60' : 'rgba(255, 255, 255, 0.6)',
              fontSize: '18px',
              fontWeight: '600',
              padding: '12px 24px',
              cursor: 'pointer',
              borderBottom: activeTab === 'software3' ? '3px solid #27AE60' : '3px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.3s ease'
            }}
          >
            Software 3.0
          </button>
          <button
            onClick={() => setActiveTab('genetic')}
            style={{
              background: 'none',
              border: 'none',
              color: activeTab === 'genetic' ? '#F39C12' : 'rgba(255, 255, 255, 0.6)',
              fontSize: '18px',
              fontWeight: '600',
              padding: '12px 24px',
              cursor: 'pointer',
              borderBottom: activeTab === 'genetic' ? '3px solid #F39C12' : '3px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.3s ease'
            }}
          >
            Genetic Evolution
          </button>
          <button
            onClick={() => setActiveTab('comparison')}
            style={{
              background: 'none',
              border: 'none',
              color: activeTab === 'comparison' ? '#F39C12' : 'rgba(255, 255, 255, 0.6)',
              fontSize: '18px',
              fontWeight: '600',
              padding: '12px 24px',
              cursor: 'pointer',
              borderBottom: activeTab === 'comparison' ? '3px solid #F39C12' : '3px solid transparent',
              marginBottom: '-2px',
              transition: 'all 0.3s ease'
            }}
          >
            Comparison
          </button>
        </div>
        
        {/* Tab Content */}
        {activeTab === 'overview' && (
          <>
            {/* Visual Timeline */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: '40px',
              marginBottom: '40px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h2 style={{ margin: '0 0 30px 0', fontSize: '28px' }}>
                The Evolution of Software Paradigms
              </h2>
              
              {/* Timeline visualization */}
              <div style={{ position: 'relative', padding: '40px 0' }}>
                {/* Timeline line */}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50px',
                  right: '50px',
                  height: '4px',
                  background: 'linear-gradient(to right, #3498DB, #E74C3C, #27AE60)',
                  borderRadius: '2px'
                }}></div>
                
                {/* Timeline nodes */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  position: 'relative'
                }}>
                  {[
                    { year: '1940s', label: 'Software 1.0', color: '#3498DB', icon: '💻' },
                    { year: '2012', label: 'Software 2.0', color: '#E74C3C', icon: '🧠' },
                    { year: '2025', label: 'Software 3.0', color: '#27AE60', icon: '🤖' }
                  ].map((item, idx) => (
                    <div key={idx} style={{ 
                      textAlign: 'center',
                      flex: 1
                    }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: item.color,
                        margin: '0 auto 15px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '28px',
                        border: '4px solid #0F2027',
                        position: 'relative',
                        zIndex: 2
                      }}>
                        {item.icon}
                      </div>
                      <div style={{ fontSize: '20px', fontWeight: '600', marginBottom: '5px' }}>
                        {item.label}
                      </div>
                      <div style={{ fontSize: '16px', opacity: 0.7 }}>
                        {item.year}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div style={{
                marginTop: '40px',
                padding: '20px',
                background: 'rgba(155, 89, 182, 0.1)',
                borderRadius: '12px',
                border: '1px solid rgba(155, 89, 182, 0.3)',
                textAlign: 'center'
              }}>
                <p style={{ margin: 0, fontSize: '18px', lineHeight: '1.8' }}>
                  <strong style={{ color: '#9B59B6' }}>Artemis</strong> evolves with each paradigm, 
                  providing intelligent optimization across all software generations
                </p>
              </div>
            </div>

            {/* Three Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
              gap: '30px'
            }}>
              <SoftwareCard
                version="Software 1.0"
                color="#3498DB"
                title="Computer Code"
                subtitle="Traditional Programming"
                timeline="1940s - Present"
                icon="💻"
                optimizations={[
                  'Code structure & architecture',
                  'Performance optimization',
                  'Memory management',
                  'Security hardening',
                  'API design & efficiency'
                ]}
                examples={['Web applications', 'Desktop software', 'System utilities', 'APIs & Services']}
              />
              
              <SoftwareCard
                version="Software 2.0"
                color="#E74C3C"
                title="Weights"
                subtitle="Machine Learning Models"
                timeline="2012 - Present"
                icon="🧠"
                optimizations={[
                  'Model architecture design',
                  'Hyperparameter tuning',
                  'Training efficiency',
                  'Model compression',
                  'Inference optimization'
                ]}
                examples={['Computer vision', 'NLP models', 'Recommendation systems', 'Time series prediction']}
              />
              
              <SoftwareCard
                version="Software 3.0"
                color="#27AE60"
                title="Prompts"
                subtitle="LLM Applications"
                timeline="2025 - Future"
                icon="🤖"
                optimizations={[
                  'Prompt engineering',
                  'Context optimization',
                  'Token efficiency',
                  'Response quality',
                  'Chain-of-thought design'
                ]}
                examples={['AI assistants', 'Code generation', 'Content creation', 'Data analysis']}
              />
            </div>

            {/* Key Insight */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(244, 81, 30, 0.1), rgba(255, 109, 0, 0.1))',
              borderRadius: '16px',
              padding: '30px',
              marginTop: '40px',
              border: '1px solid rgba(244, 81, 30, 0.3)'
            }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '20px', color: '#F39C12' }}>
                💡 The Paradigm Shift
              </h3>
              <p style={{ margin: 0, fontSize: '16px', lineHeight: '1.8' }}>
                <strong>LLM = Programmable Neural Net!</strong> Just as Software 2.0 transformed how we think about 
                computation (from explicit instructions to learned weights), Software 3.0 transforms neural networks 
                into programmable systems through natural language prompts. Artemis recognizes and optimizes for 
                each paradigm's unique characteristics.
              </p>
            </div>
          </>
        )}

        {activeTab === 'software1' && (
          <div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: '40px',
              marginBottom: '30px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '30px' }}>
                <span style={{ fontSize: '48px' }}>💻</span>
                <div>
                  <h2 style={{ margin: 0, fontSize: '32px', color: '#3498DB' }}>Software 1.0: Computer Code</h2>
                  <p style={{ margin: '10px 0 0 0', fontSize: '18px', opacity: 0.8 }}>
                    Traditional programming where humans write explicit instructions
                  </p>
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px',
                marginBottom: '30px'
              }}>
                <div style={{
                  background: 'rgba(52, 152, 219, 0.1)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(52, 152, 219, 0.3)'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#3498DB' }}>Characteristics</h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                    <li>Explicit instructions</li>
                    <li>Deterministic behavior</li>
                    <li>Human-readable code</li>
                    <li>Version control friendly</li>
                    <li>Debuggable line-by-line</li>
                  </ul>
                </div>

                <div style={{
                  background: 'rgba(155, 89, 182, 0.1)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(155, 89, 182, 0.3)'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#9B59B6' }}>Artemis Optimization</h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                    <li>Code refactoring</li>
                    <li>Performance profiling</li>
                    <li>Memory leak detection</li>
                    <li>Security vulnerability scanning</li>
                    <li>Architecture improvements</li>
                  </ul>
                </div>
              </div>

              {/* Example Optimization */}
              <div 
                style={{
                  background: 'rgba(0, 0, 0, 0.3)',
                  padding: '20px',
                  borderRadius: '12px',
                  marginTop: '20px',
                  cursor: 'pointer'
                }}
                onClick={() => toggleSection('software1-example')}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h4 style={{ margin: 0, color: '#3498DB' }}>
                    Example: API Endpoint Optimization
                  </h4>
                  <span style={{ fontSize: '12px', opacity: 0.7 }}>
                    {expandedSections['software1-example'] ? '▼' : '▶'}
                  </span>
                </div>
                
                {expandedSections['software1-example'] && (
                  <div style={{ marginTop: '20px' }}>
                    <div style={{ marginBottom: '20px' }}>
                      <h5 style={{ color: '#E74C3C', marginBottom: '10px' }}>Before Optimization:</h5>
                      <pre style={{
                        background: 'rgba(0, 0, 0, 0.5)',
                        padding: '15px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        overflow: 'auto'
                      }}>
{`def get_user_data(user_id):
    user = db.query("SELECT * FROM users WHERE id = ?", user_id)
    orders = db.query("SELECT * FROM orders WHERE user_id = ?", user_id)
    reviews = db.query("SELECT * FROM reviews WHERE user_id = ?", user_id)
    
    return {
        "user": user,
        "orders": orders,
        "reviews": reviews
    }`}
                      </pre>
                    </div>

                    <div>
                      <h5 style={{ color: '#27AE60', marginBottom: '10px' }}>After Artemis Optimization:</h5>
                      <pre style={{
                        background: 'rgba(0, 0, 0, 0.5)',
                        padding: '15px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        overflow: 'auto'
                      }}>
{`async def get_user_data(user_id: int) -> UserDataResponse:
    # Parallel database queries with connection pooling
    async with db.acquire() as conn:
        user_task = conn.fetch_one(
            "SELECT id, name, email FROM users WHERE id = $1", 
            user_id
        )
        orders_task = conn.fetch(
            "SELECT id, total, status FROM orders WHERE user_id = $1 LIMIT 10", 
            user_id
        )
        reviews_task = conn.fetch(
            "SELECT id, rating, comment FROM reviews WHERE user_id = $1 LIMIT 5", 
            user_id
        )
        
        # Execute queries in parallel
        user, orders, reviews = await asyncio.gather(
            user_task, orders_task, reviews_task
        )
    
    # Cache for 5 minutes
    return cache.set(f"user_data_{user_id}", {
        "user": user,
        "orders": orders,
        "reviews": reviews
    }, ttl=300)`}
                      </pre>
                    </div>

                    <div style={{
                      marginTop: '20px',
                      padding: '15px',
                      background: 'rgba(155, 89, 182, 0.1)',
                      borderRadius: '8px',
                      border: '1px solid rgba(155, 89, 182, 0.3)'
                    }}>
                      <strong style={{ color: '#9B59B6' }}>Improvements:</strong>
                      <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px' }}>
                        <li>3x faster with parallel queries</li>
                        <li>Connection pooling reduces overhead</li>
                        <li>Caching reduces database load by 80%</li>
                        <li>Type hints improve maintainability</li>
                        <li>Limited data fetching saves memory</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'software2' && (
          <div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: '40px',
              marginBottom: '30px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '30px' }}>
                <span style={{ fontSize: '48px' }}>🧠</span>
                <div>
                  <h2 style={{ margin: 0, fontSize: '32px', color: '#E74C3C' }}>Software 2.0: Weights</h2>
                  <p style={{ margin: '10px 0 0 0', fontSize: '18px', opacity: 0.8 }}>
                    Neural networks learn patterns from data instead of explicit programming
                  </p>
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px',
                marginBottom: '30px'
              }}>
                <div style={{
                  background: 'rgba(231, 76, 60, 0.1)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(231, 76, 60, 0.3)'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#E74C3C' }}>Characteristics</h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                    <li>Learned from data</li>
                    <li>Probabilistic outputs</li>
                    <li>Matrix operations</li>
                    <li>Requires training</li>
                    <li>Black box internals</li>
                  </ul>
                </div>

                <div style={{
                  background: 'rgba(155, 89, 182, 0.1)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(155, 89, 182, 0.3)'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#9B59B6' }}>Artemis Optimization</h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                    <li>Architecture search</li>
                    <li>Hyperparameter tuning</li>
                    <li>Model compression</li>
                    <li>Training efficiency</li>
                    <li>Inference optimization</li>
                  </ul>
                </div>
              </div>

              {/* Example Optimization */}
              <div 
                style={{
                  background: 'rgba(0, 0, 0, 0.3)',
                  padding: '20px',
                  borderRadius: '12px',
                  marginTop: '20px',
                  cursor: 'pointer'
                }}
                onClick={() => toggleSection('software2-example')}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h4 style={{ margin: 0, color: '#E74C3C' }}>
                    Example: CNN Model Optimization
                  </h4>
                  <span style={{ fontSize: '12px', opacity: 0.7 }}>
                    {expandedSections['software2-example'] ? '▼' : '▶'}
                  </span>
                </div>
                
                {expandedSections['software2-example'] && (
                  <div style={{ marginTop: '20px' }}>
                    <div style={{ marginBottom: '20px' }}>
                      <h5 style={{ color: '#E74C3C', marginBottom: '10px' }}>Before Optimization:</h5>
                      <pre style={{
                        background: 'rgba(0, 0, 0, 0.5)',
                        padding: '15px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        overflow: 'auto'
                      }}>
{`model = Sequential([
    Conv2D(32, (3, 3), activation='relu', input_shape=(224, 224, 3)),
    Conv2D(64, (3, 3), activation='relu'),
    MaxPooling2D(2, 2),
    Conv2D(128, (3, 3), activation='relu'),
    Conv2D(256, (3, 3), activation='relu'),
    MaxPooling2D(2, 2),
    Flatten(),
    Dense(512, activation='relu'),
    Dense(10, activation='softmax')
])

# Training: 100 epochs, 2 hours, 92% accuracy`}
                      </pre>
                    </div>

                    <div>
                      <h5 style={{ color: '#27AE60', marginBottom: '10px' }}>After Artemis Optimization:</h5>
                      <pre style={{
                        background: 'rgba(0, 0, 0, 0.5)',
                        padding: '15px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        overflow: 'auto'
                      }}>
{`# Optimized architecture with residual connections
model = Sequential([
    # Stem layer with batch normalization
    Conv2D(32, (3, 3), padding='same', input_shape=(224, 224, 3)),
    BatchNormalization(),
    Activation('relu'),
    
    # Depthwise separable convolutions (90% fewer parameters)
    SeparableConv2D(64, (3, 3), padding='same'),
    BatchNormalization(),
    Activation('relu'),
    MaxPooling2D(2, 2),
    
    # Residual block with skip connection
    ResidualBlock(128),
    MaxPooling2D(2, 2),
    
    # Global average pooling (no dense layers needed)
    GlobalAveragePooling2D(),
    Dense(10, activation='softmax')
])

# Mixed precision training
model = tf.keras.mixed_precision.Policy('mixed_float16')

# Training: 50 epochs, 30 minutes, 94% accuracy`}
                      </pre>
                    </div>

                    <div style={{
                      marginTop: '20px',
                      padding: '15px',
                      background: 'rgba(155, 89, 182, 0.1)',
                      borderRadius: '8px',
                      border: '1px solid rgba(155, 89, 182, 0.3)'
                    }}>
                      <strong style={{ color: '#9B59B6' }}>Improvements:</strong>
                      <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px' }}>
                        <li>4x faster training with mixed precision</li>
                        <li>75% fewer parameters with separable convolutions</li>
                        <li>2% accuracy improvement</li>
                        <li>50% less memory usage</li>
                        <li>Better generalization with batch normalization</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              {/* AlexNet Reference */}
              <div style={{
                marginTop: '30px',
                padding: '20px',
                background: 'rgba(231, 76, 60, 0.1)',
                borderRadius: '12px',
                border: '1px solid rgba(231, 76, 60, 0.3)'
              }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#E74C3C' }}>
                  Historical Context: AlexNet (2012)
                </h4>
                <p style={{ margin: 0, lineHeight: '1.8' }}>
                  The ImageNet moment that started Software 2.0. AlexNet showed that deep neural networks 
                  could outperform hand-crafted features, marking the beginning of the deep learning era.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'software3' && (
          <div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: '40px',
              marginBottom: '30px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '30px' }}>
                <span style={{ fontSize: '48px' }}>🤖</span>
                <div>
                  <h2 style={{ margin: 0, fontSize: '32px', color: '#27AE60' }}>Software 3.0: Prompts</h2>
                  <p style={{ margin: '10px 0 0 0', fontSize: '18px', opacity: 0.8 }}>
                    Programming neural networks with natural language
                  </p>
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px',
                marginBottom: '30px'
              }}>
                <div style={{
                  background: 'rgba(39, 174, 96, 0.1)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(39, 174, 96, 0.3)'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#27AE60' }}>Characteristics</h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                    <li>Natural language interface</li>
                    <li>Context-dependent behavior</li>
                    <li>Few-shot learning</li>
                    <li>Emergent capabilities</li>
                    <li>Prompt engineering</li>
                  </ul>
                </div>

                <div style={{
                  background: 'rgba(155, 89, 182, 0.1)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(155, 89, 182, 0.3)'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#9B59B6' }}>Artemis Optimization</h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                    <li>Prompt refinement</li>
                    <li>Context window management</li>
                    <li>Token optimization</li>
                    <li>Chain design</li>
                    <li>Response validation</li>
                  </ul>
                </div>
              </div>

              {/* Example Optimization */}
              <div 
                style={{
                  background: 'rgba(0, 0, 0, 0.3)',
                  padding: '20px',
                  borderRadius: '12px',
                  marginTop: '20px',
                  cursor: 'pointer'
                }}
                onClick={() => toggleSection('software3-example')}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h4 style={{ margin: 0, color: '#27AE60' }}>
                    Example: Data Analysis Prompt Optimization
                  </h4>
                  <span style={{ fontSize: '12px', opacity: 0.7 }}>
                    {expandedSections['software3-example'] ? '▼' : '▶'}
                  </span>
                </div>
                
                {expandedSections['software3-example'] && (
                  <div style={{ marginTop: '20px' }}>
                    <div style={{ marginBottom: '20px' }}>
                      <h5 style={{ color: '#E74C3C', marginBottom: '10px' }}>Before Optimization:</h5>
                      <pre style={{
                        background: 'rgba(0, 0, 0, 0.5)',
                        padding: '15px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        overflow: 'auto'
                      }}>
{`prompt = "Analyze this sales data and tell me what's important"`}
                      </pre>
                    </div>

                    <div>
                      <h5 style={{ color: '#27AE60', marginBottom: '10px' }}>After Artemis Optimization:</h5>
                      <pre style={{
                        background: 'rgba(0, 0, 0, 0.5)',
                        padding: '15px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        overflow: 'auto'
                      }}>
{`prompt = """
You are a data analyst examining quarterly sales data.

Context:
- Industry: E-commerce
- Period: Q3 2024
- Previous quarter growth: 12%

Analyze the provided sales data and structure your response as follows:

1. KEY METRICS (3-5 bullet points)
   - Total revenue and QoQ growth
   - Top performing categories
   - Customer acquisition cost trends

2. INSIGHTS (2-3 paragraphs)
   - Significant patterns or anomalies
   - Comparison with industry benchmarks
   - Correlation between marketing spend and revenue

3. RECOMMENDATIONS (3-5 action items)
   - Specific, actionable suggestions
   - Expected impact of each recommendation
   - Priority ranking (High/Medium/Low)

Use precise percentages and support insights with data.
Format numbers with appropriate units (K, M, B).
"""

# Chain-of-thought prompting for complex calculations
cot_prompt = """
Let's calculate the customer lifetime value step by step:
1. First, find the average order value
2. Then, calculate purchase frequency
3. Finally, multiply by average customer lifespan
Show your work for each step.
"""

# Validation prompt
validation_prompt = """
Review the analysis above and verify:
- All calculations are mathematically correct
- Insights are supported by the data
- Recommendations are feasible and specific
"""`}
                      </pre>
                    </div>

                    <div style={{
                      marginTop: '20px',
                      padding: '15px',
                      background: 'rgba(155, 89, 182, 0.1)',
                      borderRadius: '8px',
                      border: '1px solid rgba(155, 89, 182, 0.3)'
                    }}>
                      <strong style={{ color: '#9B59B6' }}>Improvements:</strong>
                      <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px' }}>
                        <li>10x more accurate responses with structured output</li>
                        <li>Consistent formatting across analyses</li>
                        <li>50% reduction in hallucinations</li>
                        <li>Chain-of-thought improves calculation accuracy</li>
                        <li>Self-validation catches errors</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              {/* LLM = Programmable Neural Net */}
              <div style={{
                marginTop: '30px',
                padding: '20px',
                background: 'rgba(39, 174, 96, 0.1)',
                borderRadius: '12px',
                border: '1px solid rgba(39, 174, 96, 0.3)'
              }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#27AE60' }}>
                  Revolutionary Insight: LLM = Programmable Neural Net!
                </h4>
                <p style={{ margin: 0, lineHeight: '1.8' }}>
                  LLMs represent the first neural networks that can be programmed through natural language. 
                  Just as assembly evolved into high-level languages, prompts are becoming the new programming 
                  paradigm for AI systems.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'genetic' && (
          <ArtemisGeneticEvolution />
        )}

        {activeTab === 'comparison' && (
          <div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: '40px',
              marginBottom: '30px'
            }}>
              <h2 style={{ margin: '0 0 30px 0', fontSize: '32px' }}>
                Software Evolution Comparison
              </h2>

              {/* Comparison Table */}
              <div style={{ overflow: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  fontSize: '16px',
                  borderCollapse: 'collapse'
                }}>
                  <thead>
                    <tr>
                      <th style={{ 
                        textAlign: 'left', 
                        padding: '15px',
                        borderBottom: '2px solid rgba(255,255,255,0.2)',
                        fontWeight: '600',
                        fontSize: '18px'
                      }}>
                        Aspect
                      </th>
                      <th style={{ 
                        textAlign: 'center', 
                        padding: '15px',
                        borderBottom: '2px solid rgba(255,255,255,0.2)',
                        color: '#3498DB',
                        fontSize: '18px'
                      }}>
                        Software 1.0
                      </th>
                      <th style={{ 
                        textAlign: 'center', 
                        padding: '15px',
                        borderBottom: '2px solid rgba(255,255,255,0.2)',
                        color: '#E74C3C',
                        fontSize: '18px'
                      }}>
                        Software 2.0
                      </th>
                      <th style={{ 
                        textAlign: 'center', 
                        padding: '15px',
                        borderBottom: '2px solid rgba(255,255,255,0.2)',
                        color: '#27AE60',
                        fontSize: '18px'
                      }}>
                        Software 3.0
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { aspect: 'Programming Language', s1: 'Code (Python, Java, etc.)', s2: 'Data + Architecture', s3: 'Natural Language' },
                      { aspect: 'Development Process', s1: 'Write → Test → Deploy', s2: 'Collect → Train → Validate', s3: 'Prompt → Test → Iterate' },
                      { aspect: 'Debugging', s1: 'Line-by-line debugging', s2: 'Loss curves & metrics', s3: 'Prompt refinement' },
                      { aspect: 'Version Control', s1: 'Git (perfect diffs)', s2: 'Model checkpoints', s3: 'Prompt versioning' },
                      { aspect: 'Primary Challenge', s1: 'Logic & architecture', s2: 'Data quality & quantity', s3: 'Context & instruction clarity' },
                      { aspect: 'Optimization Focus', s1: 'CPU/Memory efficiency', s2: 'GPU utilization', s3: 'Token efficiency' },
                      { aspect: 'Artemis Role', s1: 'Code optimizer', s2: 'Model architect', s3: 'Prompt engineer' }
                    ].map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ 
                          padding: '12px 15px',
                          borderBottom: '1px solid rgba(255,255,255,0.1)',
                          fontWeight: '500'
                        }}>
                          {row.aspect}
                        </td>
                        <td style={{ 
                          padding: '12px 15px',
                          borderBottom: '1px solid rgba(255,255,255,0.1)',
                          textAlign: 'center',
                          color: '#AED6F1'
                        }}>
                          {row.s1}
                        </td>
                        <td style={{ 
                          padding: '12px 15px',
                          borderBottom: '1px solid rgba(255,255,255,0.1)',
                          textAlign: 'center',
                          color: '#F5B7B1'
                        }}>
                          {row.s2}
                        </td>
                        <td style={{ 
                          padding: '12px 15px',
                          borderBottom: '1px solid rgba(255,255,255,0.1)',
                          textAlign: 'center',
                          color: '#ABEBC6'
                        }}>
                          {row.s3}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Artemis Unified Approach */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(155, 89, 182, 0.1), rgba(142, 68, 173, 0.1))',
              borderRadius: '16px',
              padding: '40px',
              border: '2px solid rgba(155, 89, 182, 0.3)'
            }}>
              <h3 style={{ margin: '0 0 25px 0', fontSize: '28px', color: '#9B59B6' }}>
                🚀 Artemis: The Unified Optimizer
              </h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                gap: '25px'
              }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  padding: '25px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#9B59B6' }}>Universal Principles</h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                    <li>Performance optimization</li>
                    <li>Resource efficiency</li>
                    <li>Quality assurance</li>
                    <li>Iterative improvement</li>
                    <li>Human-in-the-loop control</li>
                  </ul>
                </div>

                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  padding: '25px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#9B59B6' }}>Adaptive Techniques</h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                    <li>Static analysis for code</li>
                    <li>Neural architecture search for ML</li>
                    <li>Prompt engineering for LLMs</li>
                    <li>Cross-paradigm insights</li>
                    <li>Evolutionary algorithms</li>
                  </ul>
                </div>
              </div>

              <div style={{
                marginTop: '30px',
                padding: '20px',
                background: 'rgba(0, 0, 0, 0.3)',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <p style={{ margin: 0, fontSize: '18px', lineHeight: '1.8' }}>
                  Artemis evolves with software paradigms, applying the right optimization 
                  techniques for each generation while maintaining a consistent approach to 
                  improvement and human control.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtemisSoftwareEvolution;